<template>
  <div class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div class="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
      <div class="mt-3">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">
            {{ list ? 'Edit Email List' : 'Create Email List' }}
          </h3>
          <button
            @click="$emit('close')"
            class="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon class="w-6 h-6" />
          </button>
        </div>
        
        <form @submit.prevent="saveList" class="space-y-6">
          <!-- List Details Section -->
          <div class="space-y-4">
            <h4 class="text-md font-semibold text-gray-700 border-b pb-2">List Details</h4>
            
            <div>
              <label class="form-label">List Name</label>
              <input
                v-model="form.listName"
                type="text"
                class="input-field"
                required
              />
            </div>
            
            <div>
              <label class="form-label">Description</label>
              <textarea
                v-model="form.description"
                class="input-field"
                rows="3"
                placeholder="Optional description of this email list"
              ></textarea>
            </div>
            
            <div>
              <label class="form-label">Club (Optional)</label>
              <select
                v-model="form.clubID"
                class="input-field"
              >
                <option value="">No Club</option>
                <option
                  v-for="club in clubs"
                  :key="club.ClubID"
                  :value="club.ClubID"
                >
                  {{ club.ClubName }}
                </option>
              </select>
            </div>
          </div>

          <!-- Subscribers Section (only when editing) -->
          <div v-if="list" class="space-y-4">
            <div class="flex items-center justify-between border-b pb-2">
              <h4 class="text-md font-semibold text-gray-700">
                Subscribers ({{ subscribers.length }})
              </h4>
              <div class="flex space-x-2">
                <label class="btn-secondary cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    @change="handleCsvImport"
                    class="hidden"
                  />
                  <span class="flex items-center">
                    <ArrowUpTrayIcon class="w-4 h-4 mr-1" />
                    Import CSV
                  </span>
                </label>
                <button
                  type="button"
                  @click="showAddSubscriberForm = !showAddSubscriberForm"
                  class="btn-secondary"
                >
                  <PlusIcon class="w-4 h-4 mr-1" />
                  Add Subscriber
                </button>
              </div>
            </div>

            <!-- Add Subscriber Form -->
            <div v-if="showAddSubscriberForm" class="bg-gray-50 p-4 rounded-lg space-y-3">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label class="form-label text-sm">Email *</label>
                  <input
                    v-model="newSubscriber.email"
                    type="email"
                    class="input-field"
                    placeholder="email@example.com"
                    required
                  />
                </div>
                <div>
                  <label class="form-label text-sm">First Name</label>
                  <input
                    v-model="newSubscriber.firstName"
                    type="text"
                    class="input-field"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label class="form-label text-sm">Last Name</label>
                  <input
                    v-model="newSubscriber.lastName"
                    type="text"
                    class="input-field"
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div class="flex space-x-2">
                <button
                  type="button"
                  @click="addNewSubscriber"
                  :disabled="addingSubscriber || !newSubscriber.email"
                  class="btn-primary"
                >
                  <span v-if="addingSubscriber" class="flex items-center">
                    <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Adding...
                  </span>
                  <span v-else>Add Subscriber</span>
                </button>
                <button
                  type="button"
                  @click="cancelAddSubscriber"
                  class="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>

            <!-- Bulk Add Emails -->
            <div class="bg-gray-50 p-4 rounded-lg space-y-3">
              <label class="form-label text-sm">Bulk Add Emails</label>
              <div class="flex space-x-2">
                <textarea
                  v-model="bulkEmails"
                  class="input-field flex-1"
                  rows="3"
                  placeholder="Enter emails separated by commas or one per line&#10;email1@example.com, email2@example.com"
                ></textarea>
                <button
                  type="button"
                  @click="addBulkEmails"
                  :disabled="addingBulk || !bulkEmails.trim()"
                  class="btn-secondary self-start"
                >
                  <span v-if="addingBulk" class="flex items-center">
                    <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                    Adding...
                  </span>
                  <span v-else>Add All</span>
                </button>
              </div>
            </div>

            <!-- Subscribers List -->
            <div v-if="loadingSubscribers" class="text-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p class="text-gray-500 mt-2">Loading subscribers...</p>
            </div>
            
            <div v-else-if="subscribers.length === 0" class="text-center py-8 text-gray-500">
              No subscribers yet. Add some emails to get started.
            </div>
            
            <div v-else class="border rounded-lg overflow-hidden">
              <div class="overflow-x-auto max-h-96">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50 sticky top-0">
                    <tr>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Name</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Name</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th class="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="subscriber in subscribers" :key="subscriber.SubscriberID" class="hover:bg-gray-50">
                      <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ subscriber.Email }}</td>
                      <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{{ subscriber.FirstName || '-' }}</td>
                      <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{{ subscriber.LastName || '-' }}</td>
                      <td class="px-4 py-3 whitespace-nowrap">
                        <span :class="subscriber.IsActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'" class="px-2 py-1 text-xs font-medium rounded-full">
                          {{ subscriber.IsActive ? 'Active' : 'Inactive' }}
                        </span>
                      </td>
                      <td class="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          type="button"
                          @click="removeSubscriber(subscriber.SubscriberID)"
                          :disabled="removingSubscriber === subscriber.SubscriberID"
                          class="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          <span v-if="removingSubscriber === subscriber.SubscriberID" class="flex items-center">
                            <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-1"></div>
                            Removing...
                          </span>
                          <span v-else>Remove</span>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          
          <div class="flex items-center justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              @click="$emit('close')"
              class="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="saving"
              class="btn-primary"
            >
              <span v-if="saving" class="flex items-center">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </span>
              <span v-else>{{ list ? 'Update' : 'Create' }} List</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { listsApi } from '../services/api'
import { toast } from 'vue3-toastify'
import { 
  XMarkIcon, 
  PlusIcon, 
  ArrowUpTrayIcon 
} from '@heroicons/vue/24/outline'

const props = defineProps({
  list: {
    type: Object,
    default: null
  },
  clubs: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['close', 'saved'])

// Form state
const form = ref({
  listName: '',
  description: '',
  clubID: ''
})

const saving = ref(false)
const subscribers = ref([])
const loadingSubscribers = ref(false)
const showAddSubscriberForm = ref(false)
const addingSubscriber = ref(false)
const addingBulk = ref(false)
const removingSubscriber = ref(null)

const newSubscriber = ref({
  email: '',
  firstName: '',
  lastName: ''
})

const bulkEmails = ref('')

// Watch for list changes to populate form and fetch subscribers
watch(() => props.list, async (newList) => {
  if (newList) {
    form.value = {
      listName: newList.ListName || '',
      description: newList.Description || '',
      clubID: newList.ClubID || ''
    }
    // Fetch subscribers for this list using the newList parameter
    await fetchSubscribers(newList.ListID)
  } else {
    // Reset form for new list
    form.value = {
      listName: '',
      description: '',
      clubID: ''
    }
    subscribers.value = []
  }
  showAddSubscriberForm.value = false
  newSubscriber.value = { email: '', firstName: '', lastName: '' }
  bulkEmails.value = ''
}, { immediate: true })

// Methods
const fetchSubscribers = async (listID) => {
  // Use provided listID or fall back to props.list.ListID
  const id = listID || props.list?.ListID
  if (!id) return
  
  try {
    loadingSubscribers.value = true
    const response = await listsApi.getById(id)
    // Check response structure - it should be response.data.data.subscribers
    if (response.data?.success && response.data?.data?.subscribers) {
      subscribers.value = response.data.data.subscribers
    } else if (response.data?.data?.subscribers) {
      // Fallback if success flag is missing
      subscribers.value = response.data.data.subscribers
    } else {
      console.warn('Unexpected response structure:', response.data)
      subscribers.value = []
    }
  } catch (error) {
    console.error('Error fetching subscribers:', error)
    toast.error('Failed to fetch subscribers')
    subscribers.value = []
  } finally {
    loadingSubscribers.value = false
  }
}


const addNewSubscriber = async () => {
  if (!newSubscriber.value.email || !props.list?.ListID) return
  
  try {
    addingSubscriber.value = true
    await listsApi.addSubscriber(props.list.ListID, {
      email: newSubscriber.value.email,
      firstName: newSubscriber.value.firstName || undefined,
      lastName: newSubscriber.value.lastName || undefined
    })
    toast.success('Subscriber added successfully')
    newSubscriber.value = { email: '', firstName: '', lastName: '' }
    showAddSubscriberForm.value = false
    await fetchSubscribers()
  } catch (error) {
    console.error('Error adding subscriber:', error)
    // Error toast is handled by API interceptor
  } finally {
    addingSubscriber.value = false
  }
}

const cancelAddSubscriber = () => {
  showAddSubscriberForm.value = false
  newSubscriber.value = { email: '', firstName: '', lastName: '' }
}

const addBulkEmails = async () => {
  if (!bulkEmails.value.trim() || !props.list?.ListID) return
  
  try {
    addingBulk.value = true
    const emails = bulkEmails.value
      .split(/[,\n]/)
      .map(email => email.trim())
      .filter(email => email && email.includes('@'))
    
    if (emails.length === 0) {
      toast.error('No valid emails found')
      return
    }
    
    // Convert emails to subscriber objects
    const subscribers = emails.map(email => ({ email }))
    
    const response = await listsApi.addSubscribersBulk(props.list.ListID, subscribers)
    const added = response.data.data?.added || emails.length
    const skipped = response.data.data?.skipped || 0
    
    if (skipped > 0) {
      toast.success(`Added ${added} subscriber${added !== 1 ? 's' : ''} (${skipped} skipped - already exist)`)
    } else {
      toast.success(`Added ${added} subscriber${added !== 1 ? 's' : ''}`)
    }
    
    bulkEmails.value = ''
    await fetchSubscribers()
  } catch (error) {
    console.error('Error adding bulk emails:', error)
  } finally {
    addingBulk.value = false
  }
}

const removeSubscriber = async (subscriberId) => {
  if (!props.list?.ListID) return
  
  if (!confirm('Are you sure you want to remove this subscriber from the list?')) {
    return
  }
  
  try {
    removingSubscriber.value = subscriberId
    await listsApi.removeSubscriber(props.list.ListID, subscriberId)
    toast.success('Subscriber removed successfully')
    await fetchSubscribers()
  } catch (error) {
    console.error('Error removing subscriber:', error)
    // Error toast is handled by API interceptor
  } finally {
    removingSubscriber.value = null
  }
}

const parseCSV = (csvText) => {
  const lines = csvText.split('\n').filter(line => line.trim())
  if (lines.length === 0) return []
  
  // Try to detect if first line is header
  const firstLine = lines[0].toLowerCase()
  const hasHeader = firstLine.includes('email') || firstLine.includes('first') || firstLine.includes('last')
  const startIndex = hasHeader ? 1 : 0
  
  const subscribers = []
  
  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue
    
    // Simple CSV parsing (handles quoted fields)
    const values = []
    let current = ''
    let inQuotes = false
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j]
      
      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    values.push(current.trim())
    
    // Extract email (first column or column with @)
    let email = ''
    let firstName = ''
    let lastName = ''
    
    for (const value of values) {
      if (value.includes('@')) {
        email = value.replace(/"/g, '')
      } else if (!firstName && value) {
        firstName = value.replace(/"/g, '')
      } else if (!lastName && value) {
        lastName = value.replace(/"/g, '')
      }
    }
    
    // If no email found, use first column
    if (!email && values[0]) {
      email = values[0].replace(/"/g, '')
    }
    
    if (email && email.includes('@')) {
      subscribers.push({
        email: email.trim(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined
      })
    }
  }
  
  return subscribers
}

const handleCsvImport = async (event) => {
  if (!props.list?.ListID) {
    toast.error('Please save the list first before importing subscribers')
    event.target.value = ''
    return
  }
  
  const file = event.target.files[0]
  if (!file || !props.list?.ListID) return
  
  try {
    const text = await file.text()
    const parsedSubscribers = parseCSV(text)
    
    if (parsedSubscribers.length === 0) {
      toast.error('No valid emails found in CSV file')
      return
    }
    
    addingBulk.value = true
    
    // Add subscribers in bulk
    const response = await listsApi.addSubscribersBulk(props.list.ListID, parsedSubscribers)
    const added = response.data.data?.added || parsedSubscribers.length
    const skipped = response.data.data?.skipped || 0
    
    if (skipped > 0) {
      toast.success(`Imported ${added} subscriber${added !== 1 ? 's' : ''} from CSV (${skipped} skipped - already exist)`)
    } else {
      toast.success(`Imported ${added} subscriber${added !== 1 ? 's' : ''} from CSV`)
    }
    
    await fetchSubscribers()
  } catch (error) {
    console.error('Error importing CSV:', error)
    toast.error('Failed to import CSV file')
  } finally {
    addingBulk.value = false
    // Reset file input
    event.target.value = ''
  }
}

const saveList = async () => {
  try {
    saving.value = true
    
    if (props.list) {
      // Update existing list
      await listsApi.update(props.list.ListID, {
        listName: form.value.listName,
        description: form.value.description,
        clubID: form.value.clubID || null
      })
      toast.success('List updated successfully')
    } else {
      // Create new list
      await listsApi.create({
        listName: form.value.listName,
        description: form.value.description,
        clubID: form.value.clubID || null
      })
      toast.success('List created successfully')
    }
    
    emit('saved')
  } catch (error) {
    console.error('Error saving list:', error)
    toast.error('Failed to save list')
  } finally {
    saving.value = false
  }
}

// Lifecycle
onMounted(() => {
  // Form is already initialized by the watcher
  // Also fetch subscribers if list is already set (in case watcher didn't trigger)
  if (props.list?.ListID && subscribers.value.length === 0) {
    fetchSubscribers(props.list.ListID)
  }
})
</script>
